/**
 * API Adapter — Single file for ALL API ↔ UI key translations and payload transforms.
 *
 * RULES:
 *  1. This is the ONLY file in the codebase allowed to contain API wire-format key strings
 *     (underscores, short codes, PascalCase — whatever the API sends or expects).
 *  2. UI code imports key constants from src/constants/uiKeys.ts — never from here.
 *  3. Service files call the encode/decode functions here — never build payloads inline.
 *  4. When an API renames a key: update UI_TO_WIRE below — zero other files change.
 *  5. When a new API endpoint is added: add one encodeXxx + decodeXxx pair below.
 */

import { FILTER_KEYS, WIRE, type FilterKey } from '../constants/uiKeys';
import { getCropSeasonValue } from '../utils/cropSeason';
import { supportsTprFilter } from '../config/appConfig';
import type { EntryPerformanceTieMode } from '../config/pairwiseConstants';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Key Map — UI camelCase key → API wire key
//    Only keys that differ between UI and API need an entry.
//    Keys where UI name === API wire name are pass-through (not listed).
// ─────────────────────────────────────────────────────────────────────────────

export const UI_TO_WIRE: Partial<Record<FilterKey, string>> = {
  // Short-code → full API name (v2 uses descriptive names, not short codes)
  [FILTER_KEYS.EXPERIMENTS]: 'exps',
  [FILTER_KEYS.WATER_HOLDING_CAPACITY]: 'water_holding_capacity',
  [FILTER_KEYS.ORGANIC_MATTER]: 'organic_matter',
  [FILTER_KEYS.PRECIPITATION]: 'precipitation',
  [FILTER_KEYS.RADIATION]: 'radiation',
  [FILTER_KEYS.TEMPERATURE]: 'temperature',
  [FILTER_KEYS.HUMIDITY]: 'humidity',
  [FILTER_KEYS.GENERIC_YIELD]: 'generic_yield',
  [FILTER_KEYS.RELATIVE_MATURITY]: 'rm',
  [FILTER_KEYS.CHECK_FAO]: 'relative_maturity_fao',

  // camelCase → snake_case
  [FILTER_KEYS.TRIAL_TYPE]: 'trial_type',
  [FILTER_KEYS.TRIAL_ID]: 'trial_id',
  [FILTER_KEYS.TRIAL_SOURCE]: 'trial_source',
  [FILTER_KEYS.TRIAL_TREATMENT]: 'trial_treatment',
  [FILTER_KEYS.TEMP_PRECIP]: 'temp_precip',
  [FILTER_KEYS.TEMP_PRECIP_RAW]: 'temp_precip_raw',
  [FILTER_KEYS.HARVEST_DAY]: 'harvest_day',
  [FILTER_KEYS.HARVEST_MONTH]: 'harvest_month',
  // NOTE: no PLANTING_DATE entry — it's an inactive/legacy UI key and 'plntd' is the
  // legacy v1 wire key for planting DENSITY (v2: planting_density), decoded via the
  // WIRE_TO_UI `plntd` alias below. The active planting-date filter is
  // PLANTING_DATE_NUMBER → 'planting_date'.
  [FILTER_KEYS.PLANTING_DAY]: 'plntd_day',
  [FILTER_KEYS.PLANTING_MONTH]: 'plntd_month',
  [FILTER_KEYS.TEXTURE_HU]: 'texture_hu',
  [FILTER_KEYS.TEXTURE_RAW]: 'texture_raw',
  [FILTER_KEYS.PREVIOUS_CROP]: 'previous_crop',
  [FILTER_KEYS.WITHIN_TRIAL]: 'WithInTrial',
  [FILTER_KEYS.SCUBE_PH]: 'scube_ph',
  [FILTER_KEYS.MIN_LOCATIONS]: 'min_no_of_locations',
  [FILTER_KEYS.TPR_FILTER_TYPE]: 'tpr_filter_type',
  [FILTER_KEYS.IS_EXPERIMENTAL]: 'isexpermental',
  [FILTER_KEYS.ORTHOGONAL_MODE]: 'ORTHOGONAL_MODE_ENABLED',
  [FILTER_KEYS.ALTERNATE_TRAIT]: 'alternate_trait',

  // LATAM snake_case
  [FILTER_KEYS.CROP_SEASON]: 'crop_season',
  [FILTER_KEYS.PLANTING_WINDOW]: 'planting_window',
  [FILTER_KEYS.EXPERIMENT_STAGE]: 'exp_stage',
  [FILTER_KEYS.AREA_TYPE]: 'areatype_c',
  [FILTER_KEYS.ENV_CLASS]: 'env_class',
  [FILTER_KEYS.ENSO]: 'enso',
  [FILTER_KEYS.MESOREGION_CODE]: 'mesoregion_code',
  [FILTER_KEYS.MACROREGION_CODE]: 'macroregion_code',
  [FILTER_KEYS.STATE_CODE]: 'state_code',
  [FILTER_KEYS.PLANTING_DENSITY]: 'planting_density',
  [FILTER_KEYS.AVG_YIELD]: 'avg_ygsmn',
  [FILTER_KEYS.AVG_HARVEST_POPULATION]: 'harvest_population',
  [FILTER_KEYS.AVG_OIL_PERCENT]: 'avg_oil_p',
  [FILTER_KEYS.OIL_PERCENT]: 'oil_p',
  [FILTER_KEYS.AVG_GRAIN_MOISTURE]: 'avg_gmstp',
  [FILTER_KEYS.GRAIN_MOISTURE]: 'gmstp',
  [FILTER_KEYS.YIELD]: 'ygsmn',
  [FILTER_KEYS.RELATIVE_MATURITY_N]: 'rmt_n',
  [FILTER_KEYS.PLANTING_DATE_NUMBER]: 'planting_date',
  [FILTER_KEYS.HARVEST_DATE_NUMBER]: 'harvest_date',
  [FILTER_KEYS.TPR_TYPE]: 'tpr_type',

  // Pass-through keys — UI value equals API wire key.
  // Listed explicitly so any future API rename only requires changing this file.
  [FILTER_KEYS.ENTRIES]: 'entries',
  [FILTER_KEYS.YEAR]: 'year',
  [FILTER_KEYS.COUNTRY]: 'country',
  [FILTER_KEYS.STATE]: 'state',
  [FILTER_KEYS.TEXTURE]: 'texture',
  [FILTER_KEYS.DRAINAGE]: 'drainage',
  [FILTER_KEYS.CEC]: 'cec',
  [FILTER_KEYS.PH]: 'ph',
  [FILTER_KEYS.TILLAGE]: 'cultivation',
  [FILTER_KEYS.SCUBE]: 'scube',
  [FILTER_KEYS.WCUBE]: 'wcube',
  [FILTER_KEYS.TPP]: 'tpp',
  [FILTER_KEYS.REC]: 'rec',
  [FILTER_KEYS.RTP]: 'rtp',
  [FILTER_KEYS.GROWING_DEGREE_DAYS]: 'growing_degree_days',
  // EAME check filters — v2 API wire keys (snake_case)
  [FILTER_KEYS.CHECK_STAGE]: 'stage',
  [FILTER_KEYS.CHECK_MATURITY_GROUP]: 'maturity_group',
  [FILTER_KEYS.CHECK_HERBICIDE]: 'herbicide_technology',
  [FILTER_KEYS.CHECK_OIL_CONTENT]: 'oil_content',
  [FILTER_KEYS.CHECK_COMPANY]: 'chkcmp'
};

// Reverse map built automatically from UI_TO_WIRE, plus backward-compat decode aliases
// for old short codes that legacy API responses may still return.
const WIRE_TO_UI: Record<string, string> = {
  ...Object.fromEntries(Object.entries(UI_TO_WIRE).map(([uiKey, wireKey]) => [wireKey, uiKey])),
  // Old short-code aliases — kept so legacy responses continue to decode correctly
  awc: FILTER_KEYS.WATER_HOLDING_CAPACITY,
  om: FILTER_KEYS.ORGANIC_MATTER,
  prec: FILTER_KEYS.PRECIPITATION,
  rad: FILTER_KEYS.RADIATION,
  temp: FILTER_KEYS.TEMPERATURE,
  humi: FILTER_KEYS.HUMIDITY,
  chekstg: FILTER_KEYS.CHECK_STAGE,
  chkmg: FILTER_KEYS.CHECK_MATURITY_GROUP,
  chkherbicide: FILTER_KEYS.CHECK_HERBICIDE,
  chkoilcontent: FILTER_KEYS.CHECK_OIL_CONTENT,
  chkcmp: FILTER_KEYS.CHECK_COMPANY,
  // check-FAO: v2 encodes as relative_maturity_fao (see UI_TO_WIRE); chekrm is the legacy
  // wire key some v2 responses still return — decode both to checkFao.
  chekrm: FILTER_KEYS.CHECK_FAO,
  chks: FILTER_KEYS.ENTRIES,
  yl: FILTER_KEYS.GENERIC_YIELD,
  avg_havpn: FILTER_KEYS.AVG_HARVEST_POPULATION,
  plntd: FILTER_KEYS.PLANTING_DENSITY,
  // LATAM v1 main-filter returns the crop season under "season"
  season: FILTER_KEYS.CROP_SEASON,
  // ENSO reaches the app under three spellings already: the timeline year record uses
  // enso_category, the per-phase breakdown uses enso_phase. Decode all of them to the one
  // UI key so the weather filter populates whichever the main-filter endpoint settles on.
  enso_category: FILTER_KEYS.ENSO,
  enso_phase: FILTER_KEYS.ENSO
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. Key translation helpers — used by codecs below
// ─────────────────────────────────────────────────────────────────────────────

/** UI camelCase key → API wire key. Returns the UI key unchanged if no mapping exists. */
export const toWireKey = (uiKey: string): string => UI_TO_WIRE[uiKey as FilterKey] ?? uiKey;

/** API wire key → UI camelCase key. Returns the wire key unchanged if no mapping exists. */
export const toUiKey = (wireKey: string): string => WIRE_TO_UI[wireKey] ?? wireKey;

// WIRE lives in constants/uiKeys (single source, importable by UI + services).
// Re-exported here so existing service imports (`import { WIRE } from './apiAdapter'`) keep working.
export { WIRE };

// ─────────────────────────────────────────────────────────────────────────────
// 3. Crop aliases — one place, used by all encode functions
// ─────────────────────────────────────────────────────────────────────────────

export const CROP_ALIASES: Record<string, string> = {
  corn: 'CORNGRAIN',
  cornsilage: 'CORNSILAGE',
  sunflower: 'SUNFLOWER',
  winterwheat: 'WINTERWHEAT',
  winterbarley: 'WINTERBARLEY',
  winteroilseeds: 'WINTEROILSEEDS'
};

export const toApiCrop = (crop: string): string =>
  CROP_ALIASES[crop.toLowerCase()] ?? crop.toUpperCase();

// ─────────────────────────────────────────────────────────────────────────────
// 4. Filter encode/decode — shared utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Normalize values: object-shaped values → comma-joined key string */
const normalizeValues = (values: unknown): string => {
  if (values && typeof values === 'object' && !Array.isArray(values)) {
    return Object.keys(values as Record<string, unknown>).join(',');
  }
  // Its one call site already filters out null/blank values before calling in.
  /* v8 ignore next -- @preserve */
  return (values as string) ?? '';
};

/**
 * The "no information" bucket is carried internally under the sentinel key "null"
 * (see transformArray in workspaceFilterView). On the wire the backend expects an
 * actual null for that selection, not the literal string "null".
 */
const NO_INFO_TOKEN = 'null';

/** Array values (LATAM): each "null" token → JSON null. Non-arrays pass through. */
const encodeNoInfoArrayValues = (values: unknown): unknown =>
  Array.isArray(values) ? values.map((v) => (v === NO_INFO_TOKEN ? null : v)) : values;

/**
 * v2 filters: the "No Information" selection (NO_INFO_TOKEN "null") is KEPT in `value`
 * alongside the real values (e.g. ["PLOUGHING","null"]) AND flagged with
 * `is_no_data_check_enabled: true`. Only genuine null/undefined are dropped. Returns the
 * value array (token retained) and whether the no-info option was present.
 */
const splitNoInfoSelection = (values: unknown): { value: unknown[]; noData: boolean } => {
  // Its one call site already filters out null values before calling in.
  /* v8 ignore next -- @preserve */
  const nonArrayValues = values == null ? [] : [values];
  const arr = Array.isArray(values) ? values : nonArrayValues;
  const noData = arr.some((v) => v === NO_INFO_TOKEN || v == null);
  // Keep the "null" token in the value array (backend expects it, e.g. ["PLOUGHING","null"]);
  // drop only genuine null/undefined.
  const value = arr.filter((v) => v != null);
  return { value, noData };
};

/** Prepend `is_no_data_check_enabled: true` to a v2 filter object when no-info is selected. */
const withNoInfoFlag = <T extends Record<string, unknown>>(base: T, noData: boolean) =>
  noData ? { is_no_data_check_enabled: true, ...base } : base;

/**
 * Range-input filter wire keys. In entry-performance / heatmap / scatter (incl.
 * ortho) request bodies these are split into `{key}_from` / `{key}_to`, each
 * shaped as { value: "<string>" }, e.g. rm → rm_from/rm_to. Shared by EAME and
 * LATAM — only keys actually present in a given request are split.
 */
export const RANGE_FILTER_WIRE_KEYS = new Set<string>([
  WIRE.PLANTING_DATE,
  WIRE.HARVEST_DATE,
  WIRE.RM,
  WIRE.GMSTP,
  WIRE.AVG_GMSTP,
  WIRE.YGSMN,
  WIRE.AVG_YGSMN,
  WIRE.PLANTING_DENSITY,
  WIRE.HARVEST_POPULATION,
  WIRE.GROWING_DEGREE_DAYS,
  WIRE.TEMP_PRECIP_RAW
]);

/**
 * Split a range filter's "from,to" value into `{key}_from` / `{key}_to` entries,
 * each `{ value: "<string>" }` (string value, no `field`). Sides that are empty
 * or "NULL" are omitted.
 */
export const splitRangeFilterValue = (
  wireKey: string,
  rawValue: unknown
): Record<string, { value: string }> => {
  const parts = String((rawValue as string | number | null | undefined) ?? '').split(',');
  const out: Record<string, { value: string }> = {};
  const keep = (v: string | undefined) => v !== undefined && v !== '' && v !== 'NULL';
  if (keep(parts[0])) out[`${wireKey}_from`] = { value: parts[0] };
  if (keep(parts[1])) out[`${wireKey}_to`] = { value: parts[1] };
  return out;
};

/**
 * LATAM soy RM (rmt_n) range → `rmt_n_from` / `rmt_n_to`, each `{ field: 'rmt_n', value }`
 * with a numeric value. Shared by entry-performance, heatmap/scatter and timeline so the
 * parsing lives in one place. The backend requires both bounds together ("Both 'rmt_n_from'
 * and 'rmt_n_to' must be provided together"), so anything but exactly two numeric bounds —
 * e.g. "NULL,5.3" or "3,7,9" — returns {} and sends no RM filter.
 *
 * `includeField: false` keeps an endpoint's existing `{ value }` shape: heatmap/scatter have
 * always sent the bounds without `field`, and that wire contract is left unchanged here.
 */
export const buildRmtNRangeFilters = (
  rawValue: unknown,
  { includeField = true }: { includeField?: boolean } = {}
): Record<string, { field?: string; value: number }> => {
  let parts: string[] = [];
  if (typeof rawValue === 'string') parts = rawValue.split(',');
  else if (Array.isArray(rawValue)) parts = rawValue.map((v) => `${v}`);
  // Exactly two bounds, as the previous per-encoder copies required (e.g. "3,7,9" is dropped)
  if (parts.length !== 2) return {};
  const [from, to] = parts;
  const isNumeric = (v: string): boolean => v.trim() !== '' && Number.isFinite(Number(v));
  if (!isNumeric(from) || !isNumeric(to)) return {};
  const bound = (v: string) =>
    includeField ? { field: WIRE.RMT_N, value: Number(v) } : { value: Number(v) };
  return { rmt_n_from: bound(from), rmt_n_to: bound(to) };
};

// Custom-threshold / TPR yield ranges: "7-10" → { start: "7", end: "10" }.
// The API expects the bounds as STRINGS. Handles negative and decimal bounds too,
// e.g. "-5-5" → { start: "-5", end: "5" }, "7.5-10.2" → { start: "7.5", end: "10.2" }.
const RANGE_PATTERN = /^\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*$/;
const convertToRanges = (values: string[]): { start: string; end: string }[] =>
  values.map((range) => {
    const raw = String(range);
    const match = RANGE_PATTERN.exec(raw);
    if (match) return { start: match[1], end: match[2] };
    // Fallback for any unexpected shape: split on the first hyphen.
    const [start = '', end = ''] = raw.split('-');
    return { start, end };
  });

// ─────────────────────────────────────────────────────────────────────────────
// 5. EAME Main Filter API — encode request / decode response
//    Endpoint: POST /v2/main-filter
// ─────────────────────────────────────────────────────────────────────────────

interface EAMEMainFilterInput {
  crop: string;
  region: string;
  entry: string;
  entryType: string;
  country?: string | null;
  countryZone?: string | null;
  filters: Record<string, any>;
  userYieldUnit: string;
  isOrthogonalMode: boolean;
  thresholdType?: string;
}

/**
 * Top-level region-filter fields shared by every EAME v2 request encoder.
 *
 * Rules (EAME v2, all APIs):
 * - `is_region_filter_supported: true` when the country is empty (null/""/undefined)
 *   for ANY crop, OR a custom threshold is used, OR a TPR-supported crop carries a
 *   tpr_filter_type value.
 * - `region_filter_tpr_zone: <workspace value>` for TPR-supported crops (e.g. corn)
 *   when set. (The main-filter request ALSO keeps `tpr_filter_type` inside its filters
 *   array — that is handled in encodeEAMEMainFilterRequest, not here.)
 *
 * `tprFilterValue` must already be resolved to null for non-TPR crops by the caller.
 */
export const buildRegionFilterFields = (opts: {
  country?: string | null;
  isCustomThreshold?: boolean;
  tprFilterValue?: unknown;
}): Record<string, unknown> => {
  const countryEmpty = opts.country == null || opts.country === '';
  const tprValue = opts.tprFilterValue ?? null;
  const regionSupported = countryEmpty || !!opts.isCustomThreshold || tprValue != null;
  return {
    ...(regionSupported ? { is_region_filter_supported: true } : {}),
    ...(tprValue != null ? { region_filter_tpr_zone: tprValue } : {})
  };
};

const PRICE_GUIDANCE_FILTER_KEYS = new Set([
  'country',
  'state',
  'year',
  'trial_type',
  'trial_id',
  'location_selector',
  'experiment_id',
  'tpp',
  'texture',
  'drainage',
  'water_holding_capacity',
  'organic_matter',
  'cec',
  'ph',
  'wcube',
  'scube',
  'scube_ph',
  'temp_precip',
  'temp_precip_raw',
  'radiation',
  'prec',
  'temp',
  'humidity',
  'plntd',
  'plntd_month',
  'plntd_day',
  'harvest_month',
  'harvest_day',
  'planting_date',
  'harvest_date',
  'previous_crop',
  'tillage',
  'planting_density',
  'harvest_population',
  'growing_degree_days',
  'ygsmn',
  'avg_ygsmn',
  'gmstp',
  'avg_gmstp',
  'oil_p',
  'avg_oil_p',
  'generic_yield',
  'company',
  'current_stage_code',
  'herbicide_technology',
  'oil_content',
  'maturity_group',
  'rm',
  'competitors'
]);

const PRICE_GUIDANCE_FILTER_KEY_ALIASES: Record<string, string> = {
  exps: 'experiment_id',
  chkcmp: 'company',
  stage: 'current_stage_code',
  cultivation: 'tillage',
  temperature: 'temp',
  precipitation: 'prec'
};

const PRICE_GUIDANCE_NUMERIC_RANGES = new Set([
  'temp_precip_raw',
  'planting_density',
  'harvest_population',
  'growing_degree_days',
  'ygsmn',
  'avg_ygsmn',
  'gmstp',
  'avg_gmstp',
  'oil_p',
  'avg_oil_p',
  'rm'
]);

const toPriceGuidanceList = (rawValue: unknown): unknown[] => {
  if (Array.isArray(rawValue)) return rawValue;
  if (rawValue && typeof rawValue === 'object') return Object.keys(rawValue);
  if (typeof rawValue === 'string') return rawValue.split(',');
  return rawValue == null ? [] : [rawValue];
};

const toPriceGuidanceRange = (rawValue: unknown, numeric: boolean) => {
  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    const range = rawValue as { start?: unknown; end?: unknown };
    return { start: range.start, end: range.end };
  }
  let values: unknown[];
  if (Array.isArray(rawValue)) {
    values = rawValue;
  } else if (typeof rawValue === 'string') {
    values = rawValue.split(',');
  } else {
    values = [];
  }
  if (values.length !== 2) return null;
  const parse = (value: unknown) => {
    if (value == null || value === '' || value === 'NULL') return null;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    return numeric ? Number(value) : value.toString();
  };
  const range = { start: parse(values[0]), end: parse(values[1]) };
  if (
    numeric &&
    (range.start == null ||
      range.end == null ||
      Number.isNaN(range.start) ||
      Number.isNaN(range.end))
  )
    return null;
  return range;
};

const encodePriceGuidanceFilter = (wireKey: string, rawValue: unknown, regionMode: boolean) => {
  const splitValues = toPriceGuidanceList(rawValue);
  const { value, noData } = splitNoInfoSelection(splitValues);
  if (wireKey === 'generic_yield' && regionMode) {
    return {
      value: convertToRanges(value.filter((item): item is string => typeof item === 'string')),
      ...(noData ? { is_no_data_check_enabled: true } : {})
    };
  }
  if (PRICE_GUIDANCE_NUMERIC_RANGES.has(wireKey)) {
    return toPriceGuidanceRange(rawValue, true);
  }
  if (wireKey === 'planting_date' || wireKey === 'harvest_date') {
    return toPriceGuidanceRange(rawValue, false);
  }
  if (wireKey === 'competitors') return value;
  return { value, ...(noData ? { is_no_data_check_enabled: true } : {}) };
};

interface PriceGuidanceV2Request {
  crop: string;
  region: string;
  context: {
    entry: string;
    entry_type: string;
    country?: string;
    country_zone?: string;
  };
  min_no_of_locations?: number;
  is_region_filter_supported?: boolean;
  region_filter_tpr_zone?: unknown;
  filters: Record<string, unknown>;
}

export const encodePriceGuidanceV2Request = (input: {
  crop: string;
  region: string;
  entry: string;
  entryType: string;
  country?: string | null;
  countryZone?: string | null;
  parameters: any[];
  isOrthogonalMode?: boolean;
  thresholdType?: string;
}): PriceGuidanceV2Request => {
  const parameters = input.parameters ?? [];
  const minLocationParam = parameters.find(
    (param) => toWireKey(toUiKey(param.Key ?? param.key)) === WIRE.MIN_LOCATIONS
  );
  const tprFilterParam = supportsTprFilter(input.crop.toLowerCase())
    ? parameters.find(
        (param) => toWireKey(toUiKey(param.Key ?? param.key)) === WIRE.TPR_FILTER_TYPE
      )
    : null;
  const tprFilterValue = tprFilterParam?.Values ?? tprFilterParam?.values ?? null;
  const isCustomThreshold = input.thresholdType === 'custom';
  const regionMode = !input.country || isCustomThreshold || tprFilterValue != null;
  const filters: Record<string, unknown> = {};

  parameters.forEach((param) => {
    const wireKey = toWireKey(toUiKey(param.Key ?? param.key));
    if (wireKey === WIRE.MIN_LOCATIONS || wireKey === WIRE.TPR_FILTER_TYPE) return;
    const rawValue = param.Values ?? param.values;
    if (rawValue == null || rawValue === '') return;
    const targetKey =
      wireKey === 'entries'
        ? 'competitors'
        : (PRICE_GUIDANCE_FILTER_KEY_ALIASES[wireKey] ?? wireKey);
    if (!PRICE_GUIDANCE_FILTER_KEYS.has(targetKey)) return;
    const encodedValue = encodePriceGuidanceFilter(targetKey, rawValue, regionMode);
    if (encodedValue != null) filters[targetKey] = encodedValue;
  });

  const minNoOfLocations = minLocationParam?.Values ?? minLocationParam?.values;
  return {
    crop: toApiCrop(input.crop),
    region: input.region.toUpperCase(),
    context: {
      entry: input.entry,
      entry_type: input.entryType,
      ...(input.country ? { country: input.country } : {}),
      ...(input.countryZone ? { country_zone: input.countryZone } : {})
    },
    ...(minNoOfLocations != null && minNoOfLocations !== ''
      ? { min_no_of_locations: Number(minNoOfLocations) }
      : {}),
    ...buildRegionFilterFields({
      country: input.country,
      isCustomThreshold,
      tprFilterValue
    }),
    filters
  };
};

export const encodeEAMEMainFilterRequest = (input: EAMEMainFilterInput) => {
  const parameters = Object.values(input.filters || {})
    .flat()
    .filter((item: any) => {
      const v = item?.values ?? item?.Values;
      return v != null && v !== '';
    })
    .map((item: any) => ({
      ...item,
      values: normalizeValues(item.values ?? item.Values)
    }));

  // These keys are handled as top-level request fields — never go into the filters array.
  // v2 keeps tpr_filter_type OUT of the filters object across ALL APIs (incl. main-filter);
  // its value is surfaced only top-level as region_filter_tpr_zone (+ is_region_filter_supported)
  // via buildRegionFilterFields below. tpr_filter_type === region_filter_tpr_zone (renamed old→new).
  const EAME_MAIN_FILTER_SKIP = new Set([
    WIRE.MIN_LOCATIONS,
    WIRE.ORTHOGONAL_MODE_ENABLED,
    WIRE.TPR_FILTER_TYPE
  ]);
  // Canonicalize item.key ?? item.Key the same tolerant way the TPR lookup below does -
  // a capital-Key param used to slip past this check (lowercase-only) and leak into
  // the filters array instead of being skipped.
  const filteredParams = parameters.filter(
    (item: any) => !EAME_MAIN_FILTER_SKIP.has(toWireKey(toUiKey(item.key ?? item.Key)))
  );

  const minLocItem = parameters.find((item: any) => toWireKey(item.key) === WIRE.MIN_LOCATIONS);
  const minNoOfLocations = minLocItem?.values ?? 0;

  const cropLower = input.crop.toLowerCase();
  const hasTprFilter = supportsTprFilter(cropLower);
  // Tolerant of UI/wire key and Values/values casing.
  const tprFilterItem = hasTprFilter
    ? parameters.find(
        (item: any) => toWireKey(toUiKey(item.key ?? item.Key)) === WIRE.TPR_FILTER_TYPE
      )
    : null;
  const regionFilterTprZone = tprFilterItem?.values ?? tprFilterItem?.Values ?? null;
  const isCustomThreshold = input.thresholdType === 'custom';

  return {
    crop: input.crop,
    region: input.region,
    context: {
      entry: input.entry,
      entry_type: input.entryType,
      // Omit country when empty/undefined (region-level request).
      ...(input.country ? { country: input.country } : {}),
      ...(input.countryZone ? { country_zone: input.countryZone } : {})
    },
    filters: filteredParams.map((item: any) => ({
      Key: toWireKey(toUiKey(item.key ?? item.Key)),
      RParam: item.rParam ? toWireKey(item.rParam) : toWireKey(toUiKey(item.key ?? item.Key)),
      Title: item.title,
      // Keep the "null" no-info token in the CSV (e.g. "PLOUGHING,null") — consistent with
      // the array-style v2 encoders; backend expects the token retained.
      Values: item.values
    })),
    min_no_of_locations: minNoOfLocations,
    is_export_raw_data: false,
    busy_loading: true,
    is_orthogonal_mode: input.isOrthogonalMode,
    unit_type: input.userYieldUnit,
    ...buildRegionFilterFields({
      country: input.country,
      isCustomThreshold,
      tprFilterValue: regionFilterTprZone
    })
  };
};

/** Decode EAME main-filter API response: translate wire keys → UI camelCase keys */
export const decodeEAMEMainFilterResponse = (rawFilters: any[]): any[] => {
  if (!Array.isArray(rawFilters)) return [];
  return rawFilters.map((filter: any) => {
    // Trimmed before lookup: filterKey routes an option to its attribute, so stray whitespace
    // silently drops every option carrying it — the drawer renders the survivors and gives no
    // hint the rest existed. Whitespace is never meaningful in a wire key, so normalise it
    // here rather than have one mis-keyed row cost a filter. The option's own `key` is left
    // alone: that is a data value, and it is what gets sent back in the request.
    const rawFilterKey = filter.filterKey ?? filter.FilterKey;
    const wireFilterKey = typeof rawFilterKey === 'string' ? rawFilterKey.trim() : rawFilterKey;
    return {
      ...filter,
      key: toUiKey(filter.key ?? filter.Key ?? 'null'),
      ...(wireFilterKey != null ? { filterKey: toUiKey(wireFilterKey) } : {}),
      rParam: toUiKey(filter.rParam ?? filter.RParam ?? filter.key ?? filter.Key ?? '')
    };
  });
};

/**
 * Decode LATAM v1 main-filter API response: translate wire keys → UI camelCase keys.
 * Same key translation as EAME — the LATAM response is otherwise identical in shape,
 * and the filter-view config is keyed by UI keys, so the response must be decoded or
 * attributes whose wire key differs (yl, env_class, areatype_c, chks, season, …) find
 * no data and don't render in the drawer.
 */
export const decodeLATAMFilterResponse = (rawFilters: any[]): any[] =>
  decodeEAMEMainFilterResponse(rawFilters);

// ─────────────────────────────────────────────────────────────────────────────
// 7. EAME Entry Performance API v2 — encode request (new format)
//    Endpoint: POST /v2/entry-performance
// ─────────────────────────────────────────────────────────────────────────────

interface EAMEEntryPerfV2Input {
  crop: string;
  region: string;
  entry: string;
  entryType: string;
  country?: string | null;
  countryZone?: string | null;
  parameters: any[];
  userYieldUnit: string;
  isOrthogonalMode: boolean;
  isExportRawData?: boolean;
  thresholdType?: string;
  unitAliase: Record<string, string>;
  /** Trait codes selected via Pairwise "Customize Table Columns" (Alternate Performance Indicators). */
  selectedTraitCodes?: string[];
}

/**
 * Build the EAME v2 `filters: {}` object from UI parameters. Shared by every EAME
 * v2 request (entry-performance, heatmap, scatter, ortho). Conventions:
 *  - entries        → { field: <entryType>, value: [...] }
 *  - generic_yield  → { value: [...] } (or convertToRanges when tpr/custom)
 *  - range inputs   → {key}_from / {key}_to, each { value: "<string>" }
 *  - everything else→ { value: [...] } (no `field`)
 */
export const encodeEAMEV2Filters = (
  parameters: any[],
  entryType: string,
  opts: { skipUiKeys?: string[]; regionFilterTprZone?: any; isCustomThreshold?: boolean } = {}
): Record<string, any> => {
  const { skipUiKeys = [], regionFilterTprZone = null, isCustomThreshold = false } = opts;
  const filters: Record<string, any> = {};
  // Compare skips by canonical wire key so a skip like "minLocations" also excludes
  // an existing-workspace filter keyed "min_no_of_locations" (and vice versa).
  const skipWireKeys = new Set(skipUiKeys.map((k) => toWireKey(toUiKey(k))));
  (parameters || [])
    .filter((p: any) => {
      const uiKey = p.Key ?? p.key;
      return (
        !skipWireKeys.has(toWireKey(toUiKey(uiKey))) &&
        (p.Values ?? p.values) != null &&
        (p.Values ?? p.values) !== ''
      );
    })
    .forEach((param: any) => {
      const uiKey = param.Key ?? param.key;
      // Canonicalize legacy short codes (yl/chks) / wire / UI keys to one wire key.
      const wk = toWireKey(toUiKey(uiKey));
      const rawValues = param.Values ?? param.values;
      const splitValues = typeof rawValues === 'string' ? rawValues.split(',') : rawValues;
      // "No Information" selection → is_no_data_check_enabled: true with the token
      // stripped from `value`. `value` is [] when no-info was the only selection.
      const { value: values, noData } = splitNoInfoSelection(splitValues);

      if (wk === WIRE.ENTRIES) {
        filters[WIRE.ENTRIES] = withNoInfoFlag(
          { field: entryType.toLowerCase(), value: values },
          noData
        );
      } else if (wk === WIRE.GENERIC_YIELD) {
        filters[WIRE.GENERIC_YIELD] = withNoInfoFlag(
          { value: regionFilterTprZone || isCustomThreshold ? convertToRanges(values) : values },
          noData
        );
      } else if (RANGE_FILTER_WIRE_KEYS.has(wk)) {
        // Range-input filters → {key}_from / {key}_to, each { value: "<string>" }
        // (rm, planting_date, harvest_date, …).
        Object.assign(filters, splitRangeFilterValue(wk, rawValues));
      } else {
        // Non-range filters: { value: [...] } — no `field` property.
        filters[wk] = withNoInfoFlag({ value: values }, noData);
      }
    });
  return filters;
};

export const encodeEAMEEntryPerfV2Request = (input: EAMEEntryPerfV2Input) => {
  const cropLower = input.crop.toLowerCase();
  const hasTprFilter = supportsTprFilter(cropLower);

  // min_no_of_locations is a top-level field only — it must NOT appear inside the
  // filters object (per the v2 payload, filters is empty when only min is set).
  const skipUiKeys = [
    FILTER_KEYS.MIN_LOCATIONS,
    'rmpw',
    FILTER_KEYS.TPR_FILTER_TYPE,
    FILTER_KEYS.ORTHOGONAL_MODE
  ];

  // Resolve the top-level value tolerantly — existing workspaces may key the
  // filter as "minLocations" (UI) or "min_no_of_locations" (wire).
  const minLocParam = input.parameters.find(
    (p) => toWireKey(toUiKey(p.Key ?? p.key)) === WIRE.MIN_LOCATIONS
  );
  const minNoOfLocations = minLocParam?.Values ?? minLocParam?.values ?? 0;

  // Resolve tolerantly: the tpr filter may be keyed UI ("tprFilterType") or wire
  // ("tpr_filter_type"), and its value may be under Values (capital) or values.
  const tprParam = hasTprFilter
    ? input.parameters.find((p) => toWireKey(toUiKey(p.Key ?? p.key)) === WIRE.TPR_FILTER_TYPE)
    : null;
  const regionFilterTprZone = tprParam?.Values ?? tprParam?.values ?? null;

  const isCustomThreshold = input.thresholdType === 'custom';

  const filters = encodeEAMEV2Filters(input.parameters, input.entryType, {
    skipUiKeys,
    regionFilterTprZone,
    isCustomThreshold
  });

  return {
    crop: toApiCrop(input.crop),
    region: input.region.toUpperCase(),
    context: {
      entry: input.entry,
      entry_type: input.entryType,
      // Omit country when empty/undefined (region-level request).
      ...(input.country ? { country: input.country } : {}),
      country_zone: input.countryZone ?? null
    },
    filters,
    ...buildRegionFilterFields({
      country: input.country,
      isCustomThreshold,
      tprFilterValue: regionFilterTprZone
    }),
    min_no_of_locations: minNoOfLocations,
    is_export_raw_data: input.isExportRawData ?? false,
    unit_type: input.unitAliase[input.userYieldUnit] ?? input.unitAliase['t/ha'],
    // Alternate Performance Indicators (Pairwise "Customize Table Columns") — only sent
    // when the user has actually selected trait columns, so default requests are unchanged.
    ...(input.selectedTraitCodes && input.selectedTraitCodes.length > 0
      ? {
          is_alternate_perf_indicator_enabled: true,
          alternate_perf_indicator_traits: input.selectedTraitCodes.map((code) =>
            code.toLowerCase()
          )
        }
      : {})
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. LATAM Filter Request — encode
//    Endpoint: POST /v2/filters  (and /v2/entryperformance)
// ─────────────────────────────────────────────────────────────────────────────

export const encodeEAMEExcelReportRequest = (
  input: EAMEEntryPerfV2Input & { selectedTraits: string[] }
) => ({
  ...encodeEAMEEntryPerfV2Request({
    ...input,
    thresholdType: input.country ? input.thresholdType : 'custom'
  }),
  traits: input.selectedTraits,
  busy_loading: true,
  is_orthogonal_mode: input.isOrthogonalMode
});

interface LATAMFilterInput {
  crop: string;
  region: string;
  entry: string;
  entryType: string;
  country?: string | null;
  filters: any[];
  isOrthogonalMode?: boolean;
  isExportRawData?: boolean;
  busyLoading?: boolean;
  userYieldUnit: string;
  unitAliase: Record<string, string>;
  skipFilters?: string[];
  isDefault?: boolean;
  apiType?: string; // 'filters' | 'entryperformance'
  showTie?: boolean;
  tieMode?: EntryPerformanceTieMode;
}

/**
 * Encode a single LATAM (v1) filter param into the payload. Extracted from
 * encodeLATAMFilterRequest's forEach so that function stays within cognitive-complexity
 * limits — behavior is unchanged (v1 wire shapes preserved).
 */
const applyLatamFilterParam = (
  param: any,
  payload: any,
  input: LATAMFilterInput,
  apiType: string,
  skipWire: Set<string>
): void => {
  const uiKey = param.key ?? param.Key;
  // Canonicalize so legacy short codes (yl/chks), wire keys and UI keys all
  // resolve to the same wire key (matches heatmapRequest's behavior).
  const wireKey = toWireKey(toUiKey(uiKey));
  if (skipWire.has(wireKey) || input.skipFilters?.includes(uiKey)) return;

  const rawValues = param.values ?? param.Values;
  const splitValues = typeof rawValues === 'string' ? rawValues.split(',') : rawValues;
  const values = encodeNoInfoArrayValues(splitValues);

  // LATAM UI combines ENSO "Unknown" and "No Information" into one checkbox, but v1 data
  // still contains both buckets. When user selects Unknown, send both values so filtering
  // semantics stay identical to selecting the two rows separately.
  const withCombinedEnsoUnknown = (arr: any) => {
    if (!Array.isArray(arr)) return arr;
    const hasUnknown = arr.some(
      (v) =>
        String(v ?? '')
          .trim()
          .toLowerCase() === 'unknown'
    );
    const hasNoInfo = arr.some((v) => v == null || String(v).trim().toLowerCase() === 'null');
    return hasUnknown && !hasNoInfo ? [...arr, null] : arr;
  };

  // LATAM (v1) has no generic range-input filters — the {key}_from/{key}_to
  // split is EAME-V2 only. LATAM keeps its native ygsmn_from/rmt_n_to shapes.
  switch (wireKey) {
    case WIRE.GENERIC_YIELD:
      payload.filters[WIRE.YIELD_LVL] = { field: WIRE.YIELD_LVL, value: values };
      break;
    case WIRE.ENTRIES: {
      const filterKey =
        input.isOrthogonalMode && apiType === 'entryperformance' ? WIRE.ENTRIES : WIRE.PRODUCT;
      payload.filters[filterKey] = {
        field: input.entryType.toLowerCase(),
        value: values ?? null
      };
      break;
    }
    case WIRE.AVG_YGSMN:
      // LATAM yield ranges → split _from/_to objects with numeric values.
      // avg_ygsmn uses `average_ygsmn_*` keys while keeping field `avg_ygsmn`.
      payload.filters.average_ygsmn_from = { field: WIRE.AVG_YGSMN, value: Number(values[0]) };
      payload.filters.average_ygsmn_to = { field: WIRE.AVG_YGSMN, value: Number(values[1]) };
      break;
    case WIRE.YGSMN:
      payload.filters.ygsmn_from = { field: WIRE.YGSMN, value: Number(values[0]) };
      payload.filters.ygsmn_to = { field: WIRE.YGSMN, value: Number(values[1]) };
      break;
    case WIRE.RMT_N:
      Object.assign(
        payload.filters,
        buildRmtNRangeFilters(Array.isArray(values) ? values : rawValues)
      );
      break;
    case WIRE.TEMP_PRECIP_RAW:
      payload.filters[WIRE.TEMP_PRECIP_RATIO_RANGE] = {
        field: WIRE.TEMP_PRECIP_RAW,
        value: values,
        is_no_data_check_enabled: true
      };
      break;
    case 'enso':
      // The backend filter field is enso_phase (per-phase breakdown key), not the UI/bucket
      // key 'enso'. The payload key stays 'enso'; only the inner `field` name changes.
      payload.filters[wireKey] = {
        field: 'enso_phase',
        value: withCombinedEnsoUnknown(values)
      };
      break;
    default:
      payload.filters[wireKey] = { field: wireKey, value: values };
  }
};

export const encodeLATAMFilterRequest = (input: LATAMFilterInput, apiTypeArg?: string) => {
  // Request type ('filters' | 'entryperformance'), passed as a 2nd positional
  // arg by the entry-performance / ortho callers. For entry-performance ortho it
  // switches the entry filter to `entries` (not `product`) and omits
  // is_orthogonal_mode — the /v1/*-orthogonal endpoint already signals ortho.
  const apiType = apiTypeArg ?? input.apiType ?? 'filters';

  const season = getCropSeasonValue(input.filters) ?? null;

  const minLocItem = input.filters.find((f: any) => (f.key ?? f.Key) === FILTER_KEYS.MIN_LOCATIONS);
  const minNoOfLocations = minLocItem?.values ?? minLocItem?.Values ?? 0;

  const orthomodeObj =
    input.isOrthogonalMode && apiType === 'entryperformance'
      ? {}
      : { is_orthogonal_mode: input.isOrthogonalMode ?? false };

  const payload: any = {
    crop: season ? (input.crop + season).toUpperCase() : input.crop,
    region: input.region,
    context: {
      entry: input.entry,
      entry_type: input.entryType,
      country: input.country ?? null
    },
    filters: {},
    min_no_of_locations: minNoOfLocations,
    is_export_raw_data: input.isExportRawData ?? false,
    busy_loading: input.busyLoading ?? false,
    ...orthomodeObj,
    unit_type: input.unitAliase[input.userYieldUnit] ?? input.unitAliase['t/ha'],
    ...(apiType === 'entryperformance' && input.showTie !== undefined
      ? {
          showTie: input.showTie,
          tieMode: input.tieMode ?? 'percentage'
        }
      : {})
  };

  // These are always structural params — never filters regardless of what the caller passes in skipFilters
  const LATAM_ALWAYS_SKIP_WIRE = new Set([
    WIRE.MIN_LOCATIONS,
    WIRE.CROP_SEASON,
    WIRE.ORTHOGONAL_MODE_ENABLED
  ]);

  if (!input.isDefault) {
    input.filters.forEach((param: any) =>
      applyLatamFilterParam(param, payload, input, apiType, LATAM_ALWAYS_SKIP_WIRE)
    );
  }

  return payload;
};
